import { useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight, Briefcase, CheckCircle2, GraduationCap, Loader2, Send, UserRound } from "lucide-react";
import { Stepper } from "@/pages/register/Fields";
import { directeurForm } from "@/pages/register/DirecteurForm";
import { enseignantForm } from "@/pages/register/EnseignantForm";

const EMPTY = {
  prenom: "", nom: "", sexe: "", telephone: "", email: "", fonction: "",
  school: { mode: "new", school_id: null, nom: "", type_etablissement: "", region: "", departement: "", commune: "", localite: "", ia: "", ief: "", nb_classes: "", nb_enseignants: "", nb_eleves: "", telephone: "", annee_scolaire: "2025-2026", logo_base64: null },
  niveaux: [], organisation: "", flux: "", teacher_type: "",
  username: "", usernameOk: null, recovery_email: "", password: "", password2: "", website: "",
};

function AdminDone({ data }) {
  return (
    <div className="text-center py-6" data-testid="admin-create-success">
      <div className="w-16 h-16 mx-auto rounded-full bg-[#E8F2EF] grid place-items-center"><CheckCircle2 className="w-9 h-9 text-[#1F7A63]" /></div>
      <h1 className="mt-5 text-2xl font-bold" style={{ fontFamily: "Outfit" }}>Compte créé et activé</h1>
      <p className="mt-2 text-[#5C736A]">{data.result} · {data.numero}</p>
      <Link to="/app/users"><Button className="mt-6 h-12 bg-[#1F7A63] hover:bg-[#165c4b] text-white">Retour aux utilisateurs</Button></Link>
    </div>
  );
}

function ProfileChoice({ onPick, adminMode }) {
  const cards = [
    { v: "directeur", icon: Briefcase, title: "Je suis Directeur", desc: "J'inscris mon établissement et je gère mes classes, mes enseignants, les examens et les finances." },
    { v: "maitre", icon: UserRound, title: "Je suis Enseignant", desc: "Je gère ma ou mes classes : élèves, notes, présences, bulletins. Classes simples, multigrades ou double flux." },
  ];
  return (
    <div>
      <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>{adminMode ? "Ajouter un utilisateur" : "Créer un compte"}</h1>
      <p className="mt-2 text-[#5C736A]">{adminMode ? "Mêmes formulaires que l'inscription publique — le compte sera directement activé." : "Choisissez votre profil pour commencer votre demande d'inscription."}</p>
      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
        {cards.map(({ v, icon: Icon, title, desc }) => (
          <button key={v} type="button" onClick={() => onPick(v)} data-testid={`reg-profile-${v}`}
            className="group text-left p-6 rounded-2xl border-2 border-[#E2E8F0] bg-white hover:border-[#1F7A63] hover:-translate-y-0.5 hover:shadow-lg transition-[border-color,transform,box-shadow] duration-200">
            <div className="w-12 h-12 rounded-xl bg-[#E8F2EF] grid place-items-center group-hover:bg-[#1F7A63] transition-colors">
              <Icon className="w-6 h-6 text-[#1F7A63] group-hover:text-white transition-colors" />
            </div>
            <div className="mt-4 text-lg font-bold" style={{ fontFamily: "Outfit" }}>{title}</div>
            <p className="mt-1 text-sm text-[#5C736A] leading-relaxed">{desc}</p>
            <div className="mt-4 text-sm font-semibold text-[#1F7A63] flex items-center gap-1">Continuer <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></div>
          </button>
        ))}
      </div>
      {!adminMode && <p className="mt-8 text-sm text-[#5C736A]">Déjà un compte ? <Link to="/login" className="font-semibold text-[#1F7A63] hover:underline">Se connecter</Link></p>}
    </div>
  );
}

function Success({ text, numero }) {
  return (
    <div className="text-center py-6" data-testid="reg-success">
      <div className="w-16 h-16 mx-auto rounded-full bg-[#E8F2EF] grid place-items-center"><CheckCircle2 className="w-9 h-9 text-[#1F7A63]" /></div>
      <h1 className="mt-5 text-2xl sm:text-3xl font-bold" style={{ fontFamily: "Outfit" }}>Demande envoyée</h1>
      <p className="mt-1 text-sm font-semibold text-[#B38F00]">En attente de validation</p>
      <p className="mt-4 text-[#1A2E28] max-w-md mx-auto leading-relaxed">{text}</p>
      {numero && <p className="mt-3 text-sm text-[#5C736A]">Numéro de demande : <span className="font-mono font-semibold text-[#1A2E28]" data-testid="reg-numero">{numero}</span></p>}
      <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
        <Link to="/"><Button variant="outline" className="h-12 w-full sm:w-auto">Retour à l&apos;accueil</Button></Link>
        <Link to="/login"><Button className="h-12 w-full sm:w-auto bg-[#1F7A63] hover:bg-[#165c4b] text-white">Page de connexion</Button></Link>
      </div>
    </div>
  );
}

export default function Register({ adminMode = false }) {
  const [params, setParams] = useSearchParams();
  const profil = ["directeur", "maitre"].includes(params.get("profil")) ? params.get("profil") : null;
  const cfg = profil === "directeur" ? directeurForm : enseignantForm;
  const [form, setForm] = useState(EMPTY);
  const [step, setStep] = useState(0);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const startedAt = useRef(Date.now());
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const last = useMemo(() => cfg.steps.length - 1, [cfg]);

  const pick = (v) => { setParams({ profil: v }); setStep(0); setError(null); startedAt.current = Date.now(); };
  const back = () => { setError(null); if (step === 0) setParams({}); else setStep(step - 1); window.scrollTo(0, 0); };
  const next = () => {
    const err = cfg.validate(step, form);
    setError(err);
    if (!err) { setStep(step + 1); window.scrollTo(0, 0); }
  };

  const submit = async () => {
    setBusy(true); setError(null);
    const s = form.school;
    const toInt = (v) => (v === "" || v == null ? null : Number(v));
    const body = {
      profil, teacher_type: profil === "maitre" ? form.teacher_type : null, prenom: form.prenom.trim(), nom: form.nom.trim(), sexe: form.sexe, telephone: form.telephone.trim(),
      email: form.email || null, fonction: profil === "directeur" ? form.fonction : null,
      school: { mode: s.mode, school_id: s.school_id, nom: s.nom || "—", region: s.region, departement: s.departement, commune: s.commune, localite: s.localite, ia: s.ia, ief: s.ief, telephone: s.telephone, annee_scolaire: s.annee_scolaire, logo_base64: s.logo_base64, type_etablissement: s.type_etablissement || null, nb_classes: toInt(s.nb_classes), nb_enseignants: toInt(s.nb_enseignants), nb_eleves: toInt(s.nb_eleves) },
      niveaux: profil === "maitre" ? form.niveaux : [], organisation: profil === "maitre" ? form.organisation : null, flux: form.flux || null,
      username: form.username, recovery_email: form.recovery_email || null, password: form.password,
      website: form.website, started_at: startedAt.current,
    };
    try {
      const { data } = await api.post(adminMode ? "/admin/users/full" : "/public/register", body);
      setDone(data);
      window.scrollTo(0, 0);
    } catch (e) { setError(formatError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className={adminMode ? "" : "min-h-screen bg-[#F6F8F9]"}>
      {!adminMode && <header className="bg-white border-b border-[#E2E8F0]">
        <div className="max-w-3xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="reg-home-link">
            <div className="w-9 h-9 rounded-lg bg-[#1F7A63] grid place-items-center"><GraduationCap className="w-5 h-5 text-white" /></div>
            <span className="font-bold text-lg" style={{ fontFamily: "Outfit" }}>EDUKATI</span>
          </Link>
          <Link to="/login" className="text-sm font-semibold text-[#1F7A63]">Connexion</Link>
        </div>
      </header>}
      <main className={adminMode ? "max-w-3xl" : "max-w-3xl mx-auto px-4 py-8 sm:py-12"}>
        <div className="bg-white rounded-2xl border border-[#E2E8F0] shadow-sm p-5 sm:p-8">
          {done ? (adminMode ? <AdminDone data={done} /> : <Success text={cfg.successText} numero={done.numero} />) : !profil ? <ProfileChoice onPick={pick} adminMode={adminMode} /> : (
            <form onSubmit={(e) => { e.preventDefault(); if (step < last) next(); else submit(); }} data-testid={`reg-form-${profil}`}>
              <div className="text-xs font-semibold uppercase tracking-wider text-[#1F7A63] mb-1">{profil === "directeur" ? "Inscription Directeur" : "Inscription Enseignant"}</div>
              <h1 className="text-2xl font-bold mb-6" style={{ fontFamily: "Outfit" }}>{cfg.steps[step]}</h1>
              <Stepper steps={cfg.steps} current={step} />
              <input type="text" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => set({ website: e.target.value })}
                className="absolute -left-[9999px] w-px h-px opacity-0" aria-hidden="true" />
              {cfg.render(step, form, set)}
              {error && <div className="mt-5 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3" data-testid="reg-error">{error}</div>}
              <div className="mt-8 flex flex-col-reverse sm:flex-row gap-3 sm:justify-between">
                <Button type="button" variant="outline" onClick={back} className="h-12" data-testid="reg-back"><ArrowLeft className="w-4 h-4 mr-1" /> Retour</Button>
                {step < last ? (
                  <Button type="submit" className="h-12 bg-[#1F7A63] hover:bg-[#165c4b] text-white px-8" data-testid="reg-next">Suivant <ArrowRight className="w-4 h-4 ml-1" /></Button>
                ) : (
                  <Button type="submit" disabled={busy} className="h-12 bg-[#1F7A63] hover:bg-[#165c4b] text-white px-6" data-testid="reg-submit">
                    {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}{adminMode ? "Créer et activer le compte" : cfg.submitLabel}
                  </Button>
                )}
              </div>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}
